import { queryOptions } from "@tanstack/react-query";
import { axiosInstance } from "./axios";
import { z } from "zod";
import { logger } from "@/lib/logger";

const multiplierSchema = z.object({
    id: z.number(),
    statName: z.string(),
    weight: z.number().nullable(),
    defaultWeight: z.number().nullable(),
    maxPoints: z.number().nullable(),
    defaultMaxPoints: z.number().nullable(),
    description: z.string(),
    isActive: z.boolean(),
});

const categorySchema = z.object({
    max: multiplierSchema.nullable(),
    weights: z.array(multiplierSchema).nullable(),
});

const multipliersSchema = z.object({
    offensive: z.array(categorySchema).optional(),
    defensive: z.array(categorySchema).optional(),
    teamplay: z.array(categorySchema).optional(),
    effort: z.array(categorySchema).optional(),
    bonus: z.array(categorySchema).optional(),
    total: multiplierSchema.optional(),
    gsAx: multiplierSchema.optional(),
    highDangerShots: multiplierSchema.optional(),
    lowDangerShots: multiplierSchema.optional(),
    lateralPassShots: multiplierSchema.optional(),
    screenedShots: multiplierSchema.optional(),
});

const updateMultiplierRequestSchema = z.object({
    weight: z.number().nullable().optional(),
    maxPoints: z.number().nullable().optional(),
    isActive: z.boolean(),
});

export type Multiplier = z.infer<typeof multiplierSchema>;
export type CategoryData = z.infer<typeof categorySchema>;
export type MultipliersResponse = z.infer<typeof multipliersSchema>;
export type UpdateMultiplierRequest = z.infer<typeof updateMultiplierRequestSchema>;

export const flattenMultipliers = (data: MultipliersResponse): Multiplier[] => {
    const list: Multiplier[] = [];

    try {
        for (const [category, groups] of Object.entries(data)) {
            if (
                (category === "total" ||
                    category === "gsAx" ||
                    category === "highDangerShots" ||
                    category === "lowDangerShots" ||
                    category === "lateralPassShots" ||
                    category === "screenedShots") &&
                groups &&
                !Array.isArray(groups) &&
                typeof groups === "object" &&
                "id" in groups
            ) {
                list.push(groups as Multiplier);
                continue;
            }

            if (Array.isArray(groups)) {
                for (const group of groups) {
                    if (group.max) list.push(group.max);
                    if (group.weights) list.push(...group.weights);
                }
            }
        }
    } catch (error) {
        logger.error("Error flattening multipliers:", error);
        return [];
    }

    return list;
};

const fetchMultipliers = async (): Promise<MultipliersResponse> => {
    const response = await axiosInstance.get(`/multipliers`);
    return multipliersSchema.parse(response.data);
};

export const updateMultiplier = async (id: number, data: UpdateMultiplierRequest): Promise<void> => {
    const validatedData = updateMultiplierRequestSchema.parse(data);
    await axiosInstance.put(`/multipliers/${id}`, validatedData);
};

export const resetAllMultipliers = async (): Promise<void> => {
    await axiosInstance.get("/multipliers/reset");
};

export const recalculateDatabase = async (): Promise<void> => {
    await axiosInstance.get("/multipliers/recalculate-kpis");
};

export const multipliersQueryOptions = () =>
    queryOptions({
        queryKey: ["multipliers"],
        queryFn: fetchMultipliers,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        retry: 2,
    });
